import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Bookmark as BookmarkIcon, ChevronRight, Star } from 'lucide-react';
import { currentBookmarkPath, useAddBookmark, useBookmarks, useRemoveBookmark } from '@/api/bookmarks';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Bookmarks: the header star (bookmark / un-bookmark this page) and the sidebar's Bookmarks block
 * under Home. Manage (rename, reorder, remove) at Account → Bookmarks.
 */

/** Best guess at a page's name: its H1, else the tab title without the " · …" suffixes. */
function pageTitle(): string {
  const h1 = document.querySelector('main h1')?.textContent?.trim();
  return (h1 || document.title.split(' · ')[0] || 'Page').slice(0, 120);
}

export function BookmarkButton() {
  const loc = useLocation();
  const path = currentBookmarkPath(loc);
  const { data } = useBookmarks();
  const add = useAddBookmark();
  const remove = useRemoveBookmark();
  const existing = data?.bookmarks.find((b) => b.path === path);
  if (loc.pathname === ROUTES.home) return null;
  return (
    <button type="button" disabled={add.isPending || remove.isPending}
      onClick={() => {
        if (existing) remove.mutate(existing.id, { onSuccess: () => toast.success('Bookmark removed') });
        else add.mutate({ title: pageTitle(), path }, {
          onSuccess: () => toast.success('Bookmarked', { description: 'Find it under Bookmarks in the sidebar. Rename it at Account → Bookmarks.' }),
          onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not bookmark this page'),
        });
      }}
      title={existing ? `Remove bookmark “${existing.title}”` : 'Bookmark this page'}
      aria-label={existing ? 'Remove bookmark' : 'Bookmark this page'} aria-pressed={!!existing}
      className="hidden h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground md:inline-flex">
      <Star className={cn('h-4 w-4', existing && 'fill-warning text-warning')} />
    </button>
  );
}

const OPEN_KEY = 'cosmos:nav:bookmarks';

/** Sidebar block (expanded sidebar only). Hidden until you have a bookmark. */
export function BookmarksSection({ rowClass, onNavigate }: { rowClass: string; onNavigate?: () => void }) {
  const loc = useLocation();
  const path = currentBookmarkPath(loc);
  const { data } = useBookmarks();
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(OPEN_KEY) !== '0'; } catch { return true; } });
  useEffect(() => { try { localStorage.setItem(OPEN_KEY, open ? '1' : '0'); } catch { /* storage unavailable */ } }, [open]);
  const items = data?.bookmarks ?? [];
  if (!items.length) return null;
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className={cn(rowClass, 'w-full text-muted-foreground hover:bg-muted/60 hover:text-foreground')}>
        <BookmarkIcon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">Bookmarks</span>
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <ul className="mb-1 ml-[18px] border-l border-divider pl-2">
          {items.map((b) => (
            <li key={b.id}>
              <Link to={b.path} onClick={onNavigate} title={b.path}
                className={cn('block truncate rounded-md px-2 py-1 text-[14px]', b.path === path ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')}>
                {b.title}
              </Link>
            </li>
          ))}
          <li><Link to={ROUTES.account.bookmarks} onClick={onNavigate} className="block px-2 py-0.5 text-[11.5px] text-muted-foreground hover:text-foreground">Manage…</Link></li>
        </ul>
      )}
    </div>
  );
}
